/**
 * Event header — the single published event a visitor is viewing, resolved
 * against the venue catalog. Pure state; event.sync.ts (web-side) is the
 * sole writer of eventDetailState, keyed off the event route's own params —
 * ticket-type selection and quantity are event-page.state.ts's, not carried
 * here.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { resolveLocalizedText } from "@momo/i18n";
import type { Event } from "@momo/types";
import {
	resolveVenue,
	resolveVenueTimezone,
} from "../../events/events.state.ts";
import { uiLanguage } from "../../i18n/localize.ts";
import { locationsState } from "../locations.state.ts";
import { orderAccessState } from "../order-access/order-access.state.ts";
import { refundTarget } from "../refund/request.state.ts";

export type EventDetail = Event.Event & {
	id: string;
	/**
	 * Resolved display URL for `poster` (a Storage object path per
	 * Event.Event's own field) — the same decoration events.state.ts's
	 * EventWithId carries, for the same reason: resolving it
	 * (getDownloadURL) is genuine I/O this pure state file can't perform.
	 * event.api.ts (web-side) populates it at the read seam; the designer
	 * studio's fixture sync populates it directly. null when no poster is
	 * set or the resolve failed.
	 */
	posterUrl: string | null;
	/**
	 * Resolved display URL for `logo` (a Storage object path per
	 * Event.Event's own field) — the same decoration `posterUrl` carries,
	 * for the same reason. event.api.ts (web-side) populates it at the read
	 * seam. null when no logo is set or the resolve failed.
	 */
	logoUrl: string | null;
};

export type EventDetailState = {
	readonly status: "loading" | "not-found" | "ready";
	readonly event: EventDetail | null;
};

const initial: EventDetailState = { status: "loading", event: null };

// keepOnClear: a published event is public and identity-free — a sign-in or
// sign-out elsewhere in the same tab while this event is open must not wipe
// it. Its sole writer is a live Firestore snapshot listener (event.sync.ts's
// event$) that only re-emits when the doc itself changes, so a clearStates()
// reset would strand it at `initial` with nothing to repopulate it short of
// leaving and re-entering the route.
export const eventDetailState = state<EventDetailState>(
	"storefront/event-detail",
).memory(initial, { keepOnClear: true });

/** The resolved header — the shape `event-header.view.ts` renders. Named so
 *  the view can type its own frame method against it without re-deriving it. */
export type EventHeaderReady = Extract<
	ReturnType<typeof eventHeaderView.get>,
	{ status: "ready" }
>;

/**
 * The header's view-model — a discriminated "loading"/"not-found"/"ready"
 * result, so event-header.view.ts renders exactly one of its three frames
 * (a loading placeholder, the not-found terminal, or the resolved header)
 * with no derivation of its own off the raw `eventDetailState` status.
 * "ready" carries the name, schedule, the resolved poster URL, a resolved
 * venue label with its entrance instructions, and the venue's
 * map-navigation URL. Composes "{venue} — {room}" when the event's
 * location is a room (`within` set); an unresolvable `within` fails OPEN —
 * the room's own name alone — per Location's own contract (a cross-document
 * pointer zod cannot validate). The map URL is a DIRECTIONS deep link —
 * start omitted, so the maps app navigates from the device's own position —
 * preferring the location's `geo` pin, then the parent venue's (a room
 * shares its building's pin), then the labeled address, so every resolvable
 * venue navigates. One URL for every device: the Maps URLs directions form
 * is a universal link that opens the native Google Maps app on both iOS and
 * Android when installed and the web map otherwise — the web platform
 * offers no app-chooser (`geo:` is Android-only), so a single working
 * target beats a user-agent guess. Travel mode is left to the app's own
 * default.
 */
export const eventHeaderView = computed(() => {
	// The ticket page's own order carries the event it names, frozen at
	// fulfillment — the buying page's live event doc was never loaded there.
	// A RESOLVED order is the only thing that wins here, never a pending one:
	// `orderAccessState` sits at its initial "loading" on every route that has
	// no order at all, so treating that as "the ticket page is still resolving"
	// would leave the buying page shimmering forever. order-access.sync resets
	// it on the way in and on the way out, so a stale order can never name the
	// event on a page the buyer navigated to afterwards. Reading it here,
	// rather than handing the header a property, is what lets `<event-header>`
	// mount on either route with nothing passed in.
	const order = orderAccessState.value;
	if ("event" in order) {
		const e = order.event;
		// Every line the identity block renders except one: the event's own
		// description, which OrderAccessSchema carries no field for. The
		// entrance instructions it does carry — a buyer holding a ticket is on
		// their way to the door.
		return {
			status: "ready" as const,
			id: null,
			name: e.name,
			description: null,
			poster: e.posterUrl,
			start: e.schedule.start,
			end: e.schedule.end,
			timezone: e.timezone,
			venue: e.venue,
			notes: e.notes,
			mapsUrl: e.mapsUrl,
		};
	}

	// The refund page's own picked event — a buyer naming which event they're
	// returning tickets for, resolved the same way the buying page resolves its
	// live event doc (PublishedEvent and EventDetail carry the identical shape).
	const picked = refundTarget.get();
	if (picked !== null) {
		const catalog = locationsState.value;
		const { venue, notes, mapsUrl } = resolveVenue(picked.locationId, catalog);
		return {
			status: "ready" as const,
			id: picked.id as string | null,
			name: picked.name,
			notes,
			description: resolveLocalizedText(
				picked.descriptionByLocale,
				picked.description,
				uiLanguage(),
			),
			poster: picked.posterUrl,
			start: picked.schedule.start,
			end: picked.schedule.end,
			timezone: resolveVenueTimezone(picked.locationId, catalog),
			venue,
			mapsUrl,
		};
	}

	const { status, event } = eventDetailState.value;
	if (status === "loading") return { status: "loading" as const };
	if (status !== "ready" || !event) return { status: "not-found" as const };

	const catalog = locationsState.value;
	// The same venue lines an order freezes at fulfillment — entrance
	// instructions included — resolved live off the catalog, so the identity
	// block reads the same on this route as on the ticket route and only the
	// description separates the two field sets.
	const { venue, notes, mapsUrl } = resolveVenue(event.locationId, catalog);

	return {
		status: "ready" as const,
		id: event.id as string | null,
		name: event.name,
		notes,
		description: resolveLocalizedText(
			event.descriptionByLocale,
			event.description,
			uiLanguage(),
		),
		// The artwork the buyer just tapped on the landing grid, carried into
		// the page it opens — null for an event with no poster and for a
		// failed resolve alike, which the header renders as its branded
		// fallback rather than a broken image.
		poster: event.posterUrl,
		start: event.schedule.start,
		end: event.schedule.end,
		// The venue's own zone — a Berlin 09:00 opening reads 09:00 from any
		// browser, never the visitor's own timezone. A dangling locationId
		// falls back to LocationSchema's own default, same posture as the
		// schema's `.default()`.
		timezone: resolveVenueTimezone(event.locationId, catalog),
		venue,
		mapsUrl,
	};
});
